import {
  Archive, ArchiveRestore, ArrowDown, ArrowUp, ListChecks, MoreHorizontal, Plus, Settings2,
} from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { DispositionFormDialog } from "@/features/disposition-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { moveItem, sameOrder } from "@/lib/campaign";
import { stageText } from "@/lib/contacts";
import {
  DISPOSITION_KINDS, catalogTenant, categoryTerm, kindTerm, marksDoNotCall, nextCall, orderChanges,
  reconcileOrder,
} from "@/lib/dispositions";
import {
  useDispositionCatalog, useReorderDispositions, useTenants, useUpdateDisposition,
} from "@/lib/queries";
import { atLeast, type Disposition } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The dispositions catalog (ADR-017): the outcomes a rep chooses from after a call, in the order
 * their buttons appear, and what each one does to the lead.
 *
 * One list serves the rep's buttons, the dialer's recycling, the tags the CRM receives and the
 * reports, so a slug is fixed at create and nothing is deleted: retiring is `active: false`, which
 * takes an outcome off every rep's buttons and every campaign's pick until it is restored. A kind is
 * what the dialer does next, and the page says so in words, with when the lead is called again,
 * rather than as seconds (`lib/dispositions.ts`).
 *
 * Supervisors read the active catalog. Admins see the retired outcomes too, and add, edit, retire,
 * restore and reorder; a reorder is a draft, saved as one Save. A superadmin names the tenant
 * (`?tenant=`), because core-api reads a superadmin's catalog by it. A campaign picks and orders its
 * own outcomes from this list on its Dispositions tab.
 */
export function DispositionsPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canManage = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";
  const [params, setParams] = useSearchParams();
  const tenants = useTenants({ enabled: isSuperadmin });
  const tenantId = catalogTenant(actor, tenants.data, Number(params.get("tenant")) || null);
  const catalog = useDispositionCatalog(tenantId, canManage);
  const update = useUpdateDisposition();
  const reorder = useReorderDispositions();

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Disposition | undefined>();
  const [retiring, setRetiring] = React.useState<Disposition | undefined>();
  /** The order being edited, as ids; null while nothing has been moved. */
  const [draft, setDraft] = React.useState<number[] | null>(null);

  // Another tenant's catalog is another list, and an order moved in one means nothing in the next.
  React.useEffect(() => {
    setDraft(null);
  }, [tenantId]);

  const rows = catalog.data ?? [];
  const saved = rows.map((d) => d.id);
  const order = draft ? reconcileOrder(draft, rows) : saved;
  const byId = new Map(rows.map((d) => [d.id, d]));
  const ordered = order.flatMap((id) => {
    const d = byId.get(id);
    return d ? [d] : [];
  });
  const dirty = draft !== null && !sameOrder(order, saved);

  const openForm = (d?: Disposition) => {
    setEditing(d);
    setFormOpen(true);
  };

  const setActive = async (d: Disposition, active: boolean) => {
    try {
      await update.mutateAsync({ id: d.id, active });
      toast.success(active ? "Disposition restored" : "Disposition retired", d.label);
      setRetiring(undefined);
    } catch (e) {
      toast.error(
        active ? "Could not restore the disposition" : "Could not retire the disposition",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  const saveOrder = async () => {
    try {
      await reorder.mutateAsync(orderChanges(rows, order));
      setDraft(null);
      toast.success("Order saved", "A campaign that has not picked its own shows its reps this order.");
    } catch (e) {
      toast.error("Could not save the order", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  const newButton =
    canManage && tenantId !== null ? (
      <Button variant="default" onClick={() => openForm()}>
        <Plus />
        New disposition
      </Button>
    ) : undefined;

  return (
    <Page>
      <PageHeader
        title="Dispositions"
        description="The outcomes a rep chooses from after a call, in the order their buttons appear, and what each one does to the lead: the next call, the note rule, and the stage it puts them in. A campaign picks and orders its own from this list, on its Dispositions tab."
        action={newButton}
      />

      <Card className="mb-4">
        <dl className="grid gap-x-6 gap-y-3 px-4 py-3 sm:grid-cols-2 xl:grid-cols-4">
          {DISPOSITION_KINDS.map((kind) => {
            const term = kindTerm(kind);
            return (
              <div key={kind} className="space-y-1">
                <dt>
                  <Badge tone="neutral" size="sm">
                    {term.label}
                  </Badge>
                </dt>
                <dd className="text-xs text-text-muted">{term.description}</dd>
              </div>
            );
          })}
        </dl>
        <p className="border-t border-border px-4 py-2 text-xs text-text-muted">
          <Badge tone="danger" size="sm" className="mr-1.5">
            Do not call
          </Badge>
          Whatever its kind, an outcome in the compliance category, or <span className="font-mono">dnc</span>{" "}
          itself, ends the lead for the dialer; a compliance outcome on a call with a lead also marks
          the lead do-not-call.
        </p>
      </Card>

      <Card className="overflow-hidden">
        {isSuperadmin && (tenants.data?.length ?? 0) > 1 && (
          <Toolbar>
            <Select
              value={tenantId?.toString() ?? ""}
              onValueChange={(v) => setParams({ tenant: v }, { replace: true })}
            >
              <SelectTrigger className="w-56" aria-label="Tenant">
                <SelectValue placeholder="Choose a tenant" />
              </SelectTrigger>
              <SelectContent>
                {(tenants.data ?? []).map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Toolbar>
        )}

        {(isSuperadmin && tenants.isPending) || (tenantId !== null && catalog.isPending) ? (
          <TableSkeleton cols={6} />
        ) : tenants.error ? (
          <ErrorState
            error={tenants.error}
            title="Could not load the tenants"
            action={
              <Button variant="outline" onClick={() => void tenants.refetch()}>
                Try again
              </Button>
            }
          />
        ) : tenantId === null ? (
          <EmptyState
            icon={ListChecks}
            title="No tenant to show"
            description="Dispositions belong to a tenant, and there is none to read them from."
          />
        ) : catalog.error ? (
          <ErrorState
            error={catalog.error}
            title="Could not load dispositions"
            action={
              <Button variant="outline" onClick={() => void catalog.refetch()}>
                Try again
              </Button>
            }
          />
        ) : ordered.length === 0 ? (
          <EmptyState
            icon={ListChecks}
            title="No dispositions yet"
            description="Add the outcomes a rep chooses from after a call. Until there is one, a rep has nothing to record a call with."
            action={newButton}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>Disposition</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Next call</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Stage</TableHead>
                {canManage && <TableHead className="w-28" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordered.map((d, i) => {
                const next = nextCall(d);
                return (
                  <TableRow key={d.id} className="[&>td]:h-11">
                    <TableCell className="text-xs tabular-nums text-text-subtle">{i + 1}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        <span className={cn("font-medium", d.active ? "text-text" : "text-text-muted")}>
                          {d.label}
                        </span>
                        {!d.active && (
                          <Badge tone="outline" size="sm">
                            Retired
                          </Badge>
                        )}
                      </span>
                      <span className="flex items-center gap-1 font-mono text-xs text-text-muted">
                        {d.slug}
                        <CopyButton value={d.slug} label="Copy slug" />
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex flex-wrap items-center gap-1">
                        <Badge tone="neutral" size="sm">
                          {kindTerm(d.kind).label}
                        </Badge>
                        {marksDoNotCall(d) && (
                          <Badge tone="danger" size="sm">
                            Do not call
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-text-muted">{categoryTerm(d.category)?.label ?? "—"}</TableCell>
                    <TableCell
                      className={cn("whitespace-nowrap", next.fallback ? "text-text-muted" : "text-text")}
                    >
                      {next.text}
                    </TableCell>
                    <TableCell>
                      {d.requires_note ? (
                        <Badge tone="outline" size="sm">
                          Needs a note
                        </Badge>
                      ) : (
                        <span className="text-text-subtle">—</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {/* What the outcome puts the lead in (ADR-046). A compliance outcome is Do not
                          call whatever its row says, and the column says so too. */}
                      {marksDoNotCall(d) ? (
                        <Badge tone="danger" size="sm">Do not call</Badge>
                      ) : d.stage ? (
                        <Badge tone={stageText(d.stage).tone} size="sm" title={stageText(d.stage).description}>
                          {stageText(d.stage).label}
                        </Badge>
                      ) : (
                        <span className="text-xs text-text-subtle" title="The stage stays where it is">Keeps</span>
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <span className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Move ${d.label} up`}
                            disabled={i === 0 || reorder.isPending}
                            onClick={() => setDraft(moveItem(order, i, -1))}
                          >
                            <ArrowUp />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Move ${d.label} down`}
                            disabled={i === ordered.length - 1 || reorder.isPending}
                            onClick={() => setDraft(moveItem(order, i, 1))}
                          >
                            <ArrowDown />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${d.label}`}>
                                <MoreHorizontal />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onSelect={() => openForm(d)}>
                                <Settings2 />
                                Edit disposition
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {d.active ? (
                                <DropdownMenuItem onSelect={() => setRetiring(d)}>
                                  <Archive />
                                  Retire
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onSelect={() => void setActive(d, true)}>
                                  <ArchiveRestore />
                                  Restore
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </span>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {canManage && dirty && (
        <div className="sticky bottom-0 z-10 mt-4 flex items-center justify-end gap-2 rounded-lg border border-border bg-surface px-4 py-3 shadow-xs">
          <span className="mr-auto text-xs text-text-muted">
            The order has changed. A campaign that has not picked its own shows its reps this order.
          </span>
          <Button variant="outline" disabled={reorder.isPending} onClick={() => setDraft(null)}>
            Discard
          </Button>
          <Button variant="default" loading={reorder.isPending} onClick={() => void saveOrder()}>
            Save order
          </Button>
        </div>
      )}

      <DispositionFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        disposition={editing}
        tenantId={tenantId}
        catalog={rows}
      />

      <ConfirmDialog
        open={Boolean(retiring)}
        onOpenChange={(open) => !open && setRetiring(undefined)}
        title="Retire this disposition?"
        description={
          <>
            <span className="font-medium">{retiring?.label}</span> comes off every rep's buttons, and
            off each campaign that picked it, until it is restored. Calls already given it keep it, in
            their history and in the reports.
          </>
        }
        confirmLabel="Retire disposition"
        loading={update.isPending}
        onConfirm={() => {
          if (retiring) void setActive(retiring, false);
        }}
      />
    </Page>
  );
}
